import React from 'react';
import { Button } from './ui/button';
import { Smile, Heart, ShieldCheck, Sparkles } from 'lucide-react';

const TRUST_ITEMS = [
  { Icon: Smile, label: 'Invisalign Specialist', sub: '150+ clear-aligner cases', testId: 'hero-trust-invisalign' },
  { Icon: Heart, label: 'Pain-Free Care', sub: 'Gentle, unhurried treatment', testId: 'hero-trust-pain-free' },
  { Icon: ShieldCheck, label: 'Anxious Patients Welcome', sub: 'No judgement, ever', testId: 'hero-trust-anxious' },
  { Icon: Sparkles, label: 'Modern Technology', sub: '3D scanning & digital planning', testId: 'hero-trust-technology' },
];

const HERO_IMG = 'https://images.unsplash.com/photo-1566041510639-8d95a2490bfb';
const heroSrc = (w) => `${HERO_IMG}?w=${w}&q=70&fm=webp&auto=format&fit=crop`;

/**
 * Hero section component extracted from Home.jsx
 * Displays main headline, trust indicators, Google rating, CTA buttons
 */
const HeroSection = ({ 
  onBooking, 
  onLearnMore, 
  businessRating = 4.9, 
  totalReviews = 200,
  whatsappLink 
}) => {
  return (
    <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroSrc(1280)}
          srcSet={`${heroSrc(640)} 640w, ${heroSrc(1024)} 1024w, ${heroSrc(1600)} 1600w, ${heroSrc(1920)} 1920w`}
          sizes="100vw"
          width="1920"
          height="1280"
          alt=""
          role="presentation"
          className="w-full h-full object-cover opacity-10"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#01A9B4]/20 via-white to-[#006A71]/10"></div>
      </div>
      
      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-[#006A71]/10 rounded-full border-2 border-[#01A9B4]/20">
            <p className="text-sm md:text-base text-[#006A71] font-medium">
              Private Dentist in Acton, West London — Dr Kartik Patel BDS
            </p>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-rabbit mb-6 leading-tight">
            Exceptional Dentistry,<br />
            Built to Last
          </h1>
          
          {/* Live Google Rating Pill */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#01A9B4]/10 to-[#D4AF37]/10 px-4 py-2 rounded-full border-2 border-[#01A9B4]/20 mb-6">
            <span className="text-lg md:text-xl font-bold text-[#006A71]">
              ★ {businessRating.toFixed(1)}
            </span>
            <span className="text-sm md:text-base text-chocolate-malt">
              · {totalReviews} reviews on Google
            </span>
          </div>
          
          <p className="text-lg md:text-xl text-chocolate-malt mb-8 md:mb-12">
            Natural results that function as nature intended – delivered with genuine care
          </p>

          {/* Trust Numbers */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-8 mb-8">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-burnt-coral">150+</p>
              <p className="text-sm text-chocolate-malt">Invisalign Cases</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-sky-gray">2000+</p>
              <p className="text-sm text-chocolate-malt">Happy Patients</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-burnt-coral">15+</p>
              <p className="text-sm text-chocolate-malt">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-sky-gray">{businessRating.toFixed(1)}★</p>
              <p className="text-sm text-chocolate-malt">Google Rating</p>
            </div>
          </div>

          {/* Questions CTA */}
          {whatsappLink && (
            <div className="text-center mb-8">
              <p className="text-sm text-chocolate-malt mb-3">Have questions before booking?</p>
              <Button
                size="sm"
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white"
                asChild
                aria-label="Ask us your questions on WhatsApp"
              >
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center">
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Ask on WhatsApp
                </a>
              </Button>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button 
              size="lg"
              className="w-full sm:w-auto bg-burnt-coral hover:bg-burnt-coral/90 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={onBooking}
              aria-label="Book your dental appointment now"
            >
              Book Appointment
            </Button>
            
            {whatsappLink && (
              <Button 
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                asChild
                aria-label="Contact us via WhatsApp"
              >
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  WhatsApp Us
                </a>
              </Button>
            )}
            
            <Button 
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-2 border-sky-gray text-sky-gray hover:bg-sky-gray hover:text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={onLearnMore}
              aria-label="View our dental services"
            >
              Our Services
            </Button>
          </div>

          {/* Same Day Appointments Badge */}
          <div
            className="inline-flex items-center gap-3 px-6 py-3 bg-white/80 backdrop-blur-sm border border-[#006A71]/20 rounded-full mb-12 shadow-sm"
            data-testid="hero-sameday-badge"
          >
            <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
            </span>
            <p className="text-[#006A71] font-semibold text-sm md:text-base">
              Same-day appointments often available • Send us a WhatsApp to book in
            </p>
          </div>

          {/* Trust Indicators */}
          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto text-left"
            data-testid="hero-trust-strip"
          >
            {TRUST_ITEMS.map(({ Icon, label, sub, testId }, i) => (
              <div
                key={label}
                data-testid={testId}
                role="group"
                aria-label={label}
                className="trust-card group relative flex flex-col gap-2.5 sm:gap-3 p-3.5 sm:p-5 rounded-2xl bg-white/75 backdrop-blur-md border border-[#01A9B4]/15 shadow-[0_1px_2px_rgba(26,58,74,0.04)] hover:shadow-[0_10px_30px_-12px_rgba(0,106,113,0.3)] hover:border-[#01A9B4]/40 hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <span
                  className="flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-[#006A71]/8 text-[#006A71] ring-1 ring-inset ring-[#006A71]/10 group-hover:bg-[#006A71] group-hover:text-white group-hover:ring-[#006A71] transition-colors duration-300"
                  aria-hidden="true"
                >
                  <Icon className="w-[18px] h-[18px] sm:w-5 sm:h-5" strokeWidth={1.9} />
                </span>
                <span className="block">
                  <span className="block text-[13px] sm:text-sm font-semibold text-rabbit leading-snug tracking-tight">
                    {label}
                  </span>
                  <span className="mt-0.5 block text-[11px] sm:text-xs text-chocolate-malt/70 leading-snug">
                    {sub}
                  </span>
                </span>
                <span
                  className="absolute left-3.5 sm:left-5 bottom-0 h-[2px] w-0 bg-gradient-to-r from-[#01A9B4] to-[#D4AF37] rounded-full group-hover:w-8 transition-[width] duration-300"
                  aria-hidden="true"
                ></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
